<template>
    <div>
        <div>
            <heading-title>Personal Information</heading-title>

            <div class="space-y-4">
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div class="col-span-1">
                        <field-value title="Salutation">{{ convertValue(data.primary.salutation) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Legal First Name">{{ convertValue(data.primary.firstName) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Last Name">{{ convertValue(data.primary.lastName) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Middle Initial">{{ convertValue(data.primary.middleInitial) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Suffix">{{ convertValue(data.primary.suffix) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Nickname">{{ convertValue(data.primary.nickname) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Marital Status">{{ convertValue(data.primary.maritalStatus) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Mobile Number">{{ convertValue(data.primary.mobilePhone) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Other Phone Number">{{ convertValue(data.primary.phone) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Email Address">{{ convertValue(data.primary.email) }}</field-value>
                    </div>

                    <div class="col-span-1 sm:col-span-3">
                        <field-value title="Address Line 1">{{ convertValue(data.primary.address1) }}</field-value>
                    </div>

                    <div class="col-span-1 sm:col-span-3">
                        <field-value title="Address Line 2">{{ convertValue(data.primary.address2) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="City">{{ convertValue(data.primary.city) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="State">{{ convertValue(data.primary.state) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Zip Code">{{ convertValue(data.primary.zip) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Country">{{ convertValue(data.primary.country) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Employment Status">{{ convertValue(data.primary.employment) }}</field-value>
                    </div>

                    <template v-if="data.primary.employment == 'Employed'">
                        <div class="col-span-1">&nbsp;</div>

                        <div class="col-span-1">
                            <field-value title="Occupation">{{ convertValue(data.primary.occupation) }}</field-value>
                        </div>

                        <div class="col-span-1">
                            <field-value title="Employer">{{ convertValue(data.primary.employer) }}</field-value>
                        </div>
                    </template>

                    <template v-else-if="data.primary.employment == 'Other'">
                        <div class="col-span-1">
                            <field-value title="If “Other”, please describe:">{{ convertValue(data.primary.empOther) }}</field-value>
                        </div>
                    </template>
                </div>
            </div>

            <heading-title class="mt-14">USA Patriot Act Information (required by Federal Law)</heading-title>

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-4">
                <div class="col-span-2">
                    <field-value title="Citizenship">{{ convertValue(data.primary.citizenship) }}</field-value>
                </div>

                <div class="col-span-2">
                    <field-value title="Date of Birth">{{ convertValue(data.primary.dob) }}</field-value>
                </div>

                <div class="col-span-1">
                    <field-value title="ID Type">{{ convertValue(data.primary.idType) }}</field-value>
                </div>

                <div class="col-span-1">
                    <field-value title="ID Number">{{ convertValue(data.primary.idNumber) }}</field-value>
                </div>

                <div class="col-span-1">
                    <field-value title="Exp. Date">{{ convertValue(data.primary.idExpiration) }}</field-value>
                </div>

                <div class="col-span-1">
                    <field-value title="State/Issuer">{{ convertValue(data.primary.idIssuer) }}</field-value>
                </div>

                <div class="col-span-1">
                    <field-value title="Social Security Number/Tax ID">{{ convertValue(data.primary.ssn, 'ssn') }}</field-value>
                </div>
            </div>

            <div v-if="!data.primary.kycStatus || data.primary.kycStatus == 'FAIL'" class="mt-14">
                <heading-title class="mb-2">Investor ID Verification</heading-title>
                <p class="mb-6 text-gray-500">Please upload a picture of your government ID (Example: Passport, Driver’s License, State ID, or other Govt. ID)</p>

                <app-errors :form="kycDocumentsForm" class="mb-10"></app-errors>

                <div class="block w-full max-w-lg space-y-6">
                    <div>
                        <app-downloadables v-if="kycDocumentsForm.primaryDocuments.length > 0" title="Primary Documents" :items="kycDocumentsForm.primaryDocuments"></app-downloadables>
                        <uploads-field v-else v-model="kycDocumentsForm.primaryDocuments" label="Upload your documents for verification" desc="Accepts JPG, PNG and PDF files up to 10MB" accept=".pdf,.jpg,.png" :disabled="kycDocumentsForm.busy"></uploads-field>
                    </div>

                    <div v-if="data.investingType == 'joint'">
                        <app-downloadables v-if="kycDocumentsForm.secondaryDocuments.length > 0" title="Spouse Documents" :items="kycDocumentsForm.secondaryDocuments"></app-downloadables>
                        <uploads-field v-else v-model="kycDocumentsForm.secondaryDocuments" label="Upload spouse documents for verification" desc="Accepts JPG, PNG and PDF files up to 10MB" accept=".pdf,.jpg,.png" :disabled="kycDocumentsForm.busy"></uploads-field>
                    </div>

                    <div v-if="data.investingType == 'entity'">
                        <app-downloadables v-if="kycDocumentsForm.entityDocuments.length > 0" title="Entity Documents" :items="kycDocumentsForm.entityDocuments"></app-downloadables>
                        <uploads-field v-else v-model="kycDocumentsForm.entityDocuments" label="Upload entity documents for verification" desc="Accepts JPG, PNG and PDF files up to 10MB" accept=".pdf,.jpg,.png" :disabled="kycDocumentsForm.busy"></uploads-field>
                    </div>
                </div>

                <app-button v-show="kycDocumentsForm.isDirty" @click="handleUploadKycDocuments" :busy="kycDocumentsForm.busy" class="mt-6">{{ kycButtonText }}</app-button>
            </div>

            <heading-title class="mt-14">Industry  and Other Affiliation</heading-title>

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div class="col-span-2">
                    <field-value title="Are you associated with a financial firm, investment company, bank, or regulator?">{{ convertValue(data.primary.financialAffiliation) }}</field-value>
                </div>

                <template v-if="data.primary.financialAffiliation == 'Yes'">
                    <div class="col-span-1">
                        <field-value title="Firm/Company Name">{{ convertValue(data.primary.financialFirm) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Your Position">{{ convertValue(data.primary.financialFirmPosition) }}</field-value>
                    </div>
                </template>

                <div class="col-span-2">
                    <field-value title="Are you an officer, director, or 10% or more owner of a publicly owned company?">{{ convertValue(data.primary.publicCompanyAffiliation) }}</field-value>
                </div>

                <template v-if="data.primary.publicCompanyAffiliation == 'Yes'">
                    <div class="col-span-1">
                        <field-value title="Firm/Company Name">{{ convertValue(data.primary.publicCompany) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Your Position">{{ convertValue(data.primary.publicCompanyPosition) }}</field-value>
                    </div>
                </template>

                <div class="col-span-2">
                    <field-value title="If yes, please tell us about:">{{ convertValue(data.primary.politicalAffiliation) }}</field-value>
                </div>

                <template v-if="data.primary.politicalAffiliation == 'Yes'">
                    <div class="col-span-1">
                        <field-value title="Country">{{ convertValue(data.primary.politicalCountry) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Office/Role">{{ convertValue(data.primary.politicalRole) }}</field-value>
                    </div>
                </template>
            </div>

            <heading-title class="mt-14">I plan to invest as/through:</heading-title>
            <div v-if="data.investingType != 'other'" class="flex items-center space-x-4 text-sm font-medium text-gray-700">
                <span class="uppercase">{{ convertValue(data.investingType) }}</span>
                <span v-if="data.subInvestingType"><span class="text-gray-40">/</span> {{ convertValue(data.subInvestingType) }}</span>
            </div>

            <div v-else class="flex flex-col space-y-1 text-sm font-medium text-gray-700">
                <div>Something Else</div>
                <div class="text-gray-40">{{ data.subInvestingType }}</div>
            </div>
        </div><!-- PRIMARY INFORMAITON -->

        <div v-if="data.investingType == 'joint'" class="mt-20">
            <heading-title>Spouse Personal Information</heading-title>

            <div class="space-y-4">
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div class="col-span-1">
                        <field-value title="Salutation">{{ convertValue(data.secondary.salutation) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Legal First Name">{{ convertValue(data.secondary.firstName) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Last Name">{{ convertValue(data.secondary.lastName) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Middle Initial">{{ convertValue(data.secondary.middleInitial) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Suffix">{{ convertValue(data.secondary.suffix) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Nickname">{{ convertValue(data.secondary.nickname) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Marital Status">{{ convertValue(data.secondary.maritalStatus) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Mobile Number">{{ convertValue(data.secondary.mobilePhone) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Other Phone Number">{{ convertValue(data.secondary.phone) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Email Address">{{ convertValue(data.secondary.email) }}</field-value>
                    </div>

                    <div class="col-span-1 sm:col-span-3">
                        <field-value title="Address Line 1">{{ convertValue(data.secondary.address1) }}</field-value>
                    </div>

                    <div class="col-span-1 sm:col-span-3">
                        <field-value title="Address Line 2">{{ convertValue(data.secondary.address2) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="City">{{ convertValue(data.secondary.city) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="State">{{ convertValue(data.secondary.state) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Zip Code">{{ convertValue(data.secondary.zip) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Country">{{ convertValue(data.secondary.country) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Employment Status">{{ convertValue(data.secondary.employment) }}</field-value>
                    </div>

                    <template v-if="data.secondary.employment == 'Employed'">
                        <div class="col-span-1">&nbsp;</div>

                        <div class="col-span-1">
                            <field-value title="Occupation">{{ convertValue(data.secondary.occupation) }}</field-value>
                        </div>

                        <div class="col-span-1">
                            <field-value title="Employer">{{ convertValue(data.secondary.employer) }}</field-value>
                        </div>
                    </template>

                    <template v-else-if="data.secondary.employment == 'Other'">
                        <div class="col-span-1">
                            <field-value title="If “Other”, please describe:">{{ data.secondary.empOther }}</field-value>
                        </div>
                    </template>
                </div>
            </div>

            <heading-title class="mt-14">USA Patriot Act Information (required by Federal Law)</heading-title>

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-4">
                <div class="col-span-2">
                    <field-value title="Citizenship">{{ convertValue(data.secondary.citizenship) }}</field-value>
                </div>

                <div class="col-span-2">
                    <field-value title="Date of Birth">{{ convertValue(data.secondary.dob) }}</field-value>
                </div>

                <div class="col-span-1">
                    <field-value title="ID Type">{{ convertValue(data.secondary.idType) }}</field-value>
                </div>

                <div class="col-span-1">
                    <field-value title="ID Number">{{ convertValue(data.secondary.idNumber) }}</field-value>
                </div>

                <div class="col-span-1">
                    <field-value title="Exp. Date">{{ convertValue(data.secondary.idExpiration) }}</field-value>
                </div>

                <div class="col-span-1">
                    <field-value title="State/Issuer">{{ convertValue(data.secondary.idIssuer) }}</field-value>
                </div>

                <div class="col-span-1">
                    <field-value title="Social Security Number/Tax ID">{{ convertValue(data.secondary.ssn, 'ssn') }}</field-value>
                </div>
            </div>

            <heading-title class="mt-14">Industry  and Other Affiliation</heading-title>

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div class="col-span-2">
                    <field-value title="Are you associated with a financial firm, investment company, bank, or regulator?">{{ convertValue(data.secondary.financialAffiliation) }}</field-value>
                </div>

                <template v-if="data.secondary.financialAffiliation == 'Yes'">
                    <div class="col-span-1">
                        <field-value title="Firm/Company Name">{{ convertValue(data.secondary.financialFirm) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Your Position">{{ convertValue(data.secondary.financialFirmPosition) }}</field-value>
                    </div>
                </template>

                <div class="col-span-2">
                    <field-value title="Are you an officer, director, or 10% or more owner of a publicly owned company?">{{ convertValue(data.secondary.publicCompanyAffiliation) }}</field-value>
                </div>

                <template v-if="data.secondary.publicCompanyAffiliation == 'Yes'">
                    <div class="col-span-1">
                        <field-value title="Firm/Company Name">{{ convertValue(data.secondary.publicCompany) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Your Position">{{ convertValue(data.secondary.publicCompanyPosition) }}</field-value>
                    </div>
                </template>

                <div class="col-span-2">
                    <field-value title="If yes, please tell us about:">{{ convertValue(data.secondary.politicalAffiliation) }}</field-value>
                </div>

                <template v-if="data.secondary.politicalAffiliation == 'Yes'">
                    <div class="col-span-1">
                        <field-value title="Country">{{ convertValue(data.secondary.politicalCountry) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Office/Role">{{ convertValue(data.secondary.politicalRole) }}</field-value>
                    </div>
                </template>
            </div>
        </div><!-- SECONDARY INFORMATION -->

        <div v-if="data.investingType == 'entity'" class="mt-20">
            <heading-title>Entity or Trust Information</heading-title>

            <div class="space-y-4">
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div class="col-span-1">
                        <field-value title="Legal Name of Entity or Trust">{{ convertValue(data.entity.entityName) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Federal Tax ID Number">{{ convertValue(data.entity.taxId) }}</field-value>
                    </div>

                    <div class="col-span-1 sm:col-span-2">
                        <field-value title="Entity Type">{{ convertValue(data.entity.entityType) }}</field-value>
                    </div>

                    <div class="col-span-1 sm:col-span-2">
                        <field-value title="Do you have sole signature authority?">{{ convertValue(data.entity.soleSignature) }}</field-value>
                    </div>

                    <div class="col-span-1 sm:col-span-2">
                        <field-value title="Are there more than two signatures required?">{{ convertValue(data.entity.moreSignatures) }}</field-value>
                    </div>

                    <div class="col-span-1 sm:col-span-2">
                        <field-value title="Your Title/Role within the Entity or Trust">{{ convertValue(data.entity.role) }}</field-value>
                    </div>
                </div>

                <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div class="col-span-1 sm:col-span-3">
                        <field-value title="Address Line 1">{{ data.entity.address1 }}</field-value>
                    </div>

                    <div class="col-span-1 sm:col-span-3">
                        <field-value title="Address Line 2">{{ data.entity.address2 }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="City">{{ data.entity.city }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="State">{{ convertValue(data.entity.state) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Zip Code">{{ data.entity.zip }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Country">{{ convertValue(data.entity.country) }}</field-value>
                    </div>

                    <div class="col-span-1 sm:col-span-3">
                        <field-value title="Does anyone, including you, own 25% or more of this Entity/Trust?">{{ convertValue(data.entity.else25shares) }}</field-value>
                    </div>
                </div>

                <div v-if="data.entity.soleSignature != 'Yes' && data.entity.else25shares == 'Yes'">
                    <heading-title>Beneficial Owner</heading-title>

                    <div class="divide-y divide-gray-200">
                        <div v-for="(beneficiary, index) in data.entity.beneficiary" :key="index" class="relative">
                            <div class="relative pt-4 pb-10" :class="{
                                'bg-gray-100 rounded-md !p-5 mb-5': data.entity.beneficiary.length > 1,
                                'mt-5': data.entity.beneficiary.length > 1 && index > 0
                            }">
                                <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                    <div class="col-span-1">
                                        <field-value title="First Name">{{ convertValue(beneficiary.firstName) }}</field-value>
                                    </div>

                                    <div class="col-span-1">
                                        <field-value title="Last Name">{{ convertValue(beneficiary.lastName) }}</field-value>
                                    </div>

                                    <div class="col-span-1">
                                        <field-value title="Nickname">{{ convertValue(beneficiary.nickname) }}</field-value>
                                    </div>

                                    <div class="col-span-1 sm:col-span-3">
                                        <field-value title="Address Line (Residential or Business Street Address)">{{ convertValue(beneficiary.address) }}</field-value>
                                    </div>

                                    <div class="col-span-1 sm:col-span-2">
                                        <field-value title="If US Person: Social Security Number">{{ convertValue(beneficiary.ssn, 'ssn') }}</field-value>
                                    </div>

                                    <div class="col-span-1">
                                        <field-value title="Date of Birth">{{ convertValue(beneficiary.dob) }}</field-value>
                                    </div>

                                    <div class="col-span-1 sm:col-span-3">
                                        <field-value title="For Foreign Persons: Passport Number, and Country of Issuance, or ther similar identification number">{{ convertValue(beneficiary.idNumber) }}</field-value>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div><!-- ENTITY INFORMATION -->

        <div v-if="data.investingType == 'ira'" class="mt-20">
            <heading-title>IRA Owner Information</heading-title>

            <div class="space-y-4">
                <div v-if="data.ira.hasAccount == 'Yes'" class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div class="col-span-1 sm:col-span-3">
                        <field-value title="Account Number">{{ convertValue(data.ira.accountNumber) }}</field-value>
                    </div>
                </div>

                <div v-if="data.ira.hasAccount == 'No'" class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div class="col-span-1">
                        <field-value title="Name of Current Custodian">{{ convertValue(data.ira.custodianName) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Your Name as it appears on the Account">{{ convertValue(data.ira.accountName) }}</field-value>
                    </div>

                    <div class="col-span-1 sm:col-span-2">
                        <field-value title="Account Number with Current Custodian">{{ convertValue(data.ira.accountNumber) }}</field-value>
                    </div>

                    <div class="col-span-1 sm:col-span-2">
                        <field-value title="Custodian Phone Number">{{ convertValue(data.ira.phone) }}</field-value>
                    </div>

                    <div class="col-span-1 sm:col-span-2">
                        <field-value title="Custodian Address Line 1">{{ convertValue(data.ira.address1) }}</field-value>
                    </div>

                    <div class="col-span-1 sm:col-span-2">
                        <field-value title="Custodian Address Line 1">{{ convertValue(data.ira.address1) }}</field-value>
                    </div>
                </div>

                <div v-if="data.ira.hasAccount == 'No'" class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div class="col-span-1">
                        <field-value title="City">{{ convertValue(data.ira.city) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="State">{{ convertValue(data.ira.state) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Zip Code">{{ convertValue(data.ira.zip) }}</field-value>
                    </div>

                    <div class="col-span-1 sm:col-span-3">
                        <field-value title="Country">{{ convertValue(data.ira.country) }}</field-value>
                    </div>
                </div>

                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div class="col-span-1">
                        <field-value title="Name on Credit Card">{{ convertValue(data.ira.ccName) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Credit Card Number">{{ convertValue(data.ira.ccNumber) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Expiration Date">{{ convertValue(data.ira.ccExpiration) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="CVC">{{ convertValue(data.ira.ccVerificationCode) }}</field-value>
                    </div>
                </div>

                <div v-if="data.ira.hasAccount == 'No'">
                    <heading-title>Primary Beneficiary(ies)</heading-title>

                    <div class="divide-y divide-gray-200">
                        <div v-for="(beneficiary, index) in data.ira.beneficiary" :key="index" class="relative">
                            <div class="relative pt-4 pb-10" :class="{
                                'bg-gray-100 rounded-md !p-5 mb-5': data.ira.beneficiary.length > 1,
                                'mt-5': beneficiariesNum > 1 && index > 0
                            }">
                                <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                    <div class="col-span-1">
                                        <field-value title="First Name">{{ convertValue(beneficiary.firstName) }}</field-value>
                                    </div>

                                    <div class="col-span-1">
                                        <field-value title="Last Name">{{ convertValue(beneficiary.lastName) }}</field-value>
                                    </div>

                                    <div class="col-span-1">
                                        <field-value title="Nickname">{{ convertValue(beneficiary.nickname) }}</field-value>
                                    </div>

                                    <div class="col-span-1">
                                        <field-value title="SSN or TIN">{{ convertValue(beneficiary.ssn, 'ssn') }}</field-value>
                                    </div>

                                    <div class="col-span-1">
                                        <field-value title="Relationship to IRA Owner">{{ convertValue(beneficiary.relation) }}</field-value>
                                    </div>

                                    <div class="col-span-1">
                                        <field-value title="Date of Birth">{{ convertValue(beneficiary.dob) }}</field-value>
                                    </div>

                                    <div class="col-span-1 sm:col-span-3">
                                        <field-value title="Address">{{ convertValue(beneficiary.address) }}</field-value>
                                    </div>

                                    <div class="col-span-1">
                                        <field-value title="City">{{ convertValue(beneficiary.city) }}</field-value>
                                    </div>

                                    <div class="col-span-1">
                                        <field-value title="State">{{ convertValue(beneficiary.state) }}</field-value>
                                    </div>

                                    <div class="col-span-1">
                                        <field-value title="Zip Code">{{ convertValue(beneficiary.zip) }}</field-value>
                                    </div>

                                    <div class="col-span-1 sm:col-span-3">
                                        <field-value title="Percentage">{{ convertValue(beneficiary.percentage) }}%</field-value>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import HeadingTitle from '@Components/HeadingTitle.vue'
import FieldValue from '@Components/FieldValue.vue'
import AppButton from '@Components/Button.vue'
import AppErrors from '@Components/Errors.vue'
import AppDownloadables from '@Components/Downloadables'
import UploadsField from '@Fields/Uploads'
import useForm from '@/form'
import { mask } from 'maska'
import { convertIntakeValue } from '@/utils'
import { ref } from 'vue'

export default {
    components: {
        HeadingTitle,
        FieldValue,
        AppButton,
        AppErrors,
        AppDownloadables,
        UploadsField
    },
    props: {
        data: {
            type: Object,
            required: true
        }
    },
    setup(props){
        const kycButtonText = ref('Send for Verification')

        const kycDocumentsForm = useForm({
            investingType: _.get(props.data, 'investingType'),
            primaryDocuments: _.get(props.data, 'primary.kycDocuments', []),
            secondaryDocuments: _.get(props.data, 'secondary.kycDocuments', []),
            entityDocuments: _.get(props.data, 'entity.kycDocuments', [])
        })

        function handleUploadKycDocuments(){
            kycButtonText.value = 'Submitting documents...'

            kycDocumentsForm.post(route('intake.upload.kyc')).then(response => {
                if (response.status == 200) {
                    kycButtonText.value = 'Submission Complete'

                    setTimeout(() => {
                        window.location.reload()
                    }, 1500)
                }
            }).catch(error => {
                console.log(error.response)
                kycButtonText.value = 'Submit for Verification'
            })
        }

        function convertValue(value, key = ''){
            var value = (value) ? convertIntakeValue(value) : '-'

            if (key == 'ssn') {
                value = 'XXX-XX-' + (value).substr(-4, 4)
            }

            return value
        }

        return {
            convertValue,
            kycButtonText,
            kycDocumentsForm,
            handleUploadKycDocuments
        }
    }
}
</script>

<style>

</style>
